import React from 'react';
import { Box } from '@mui/material';

import { ErrorBoundary } from 'react-error-boundary';
import { Panel, PanelGroup } from 'react-resizable-panels';

import { ResizeHandleWithTooltip } from '@watershed/shared-frontend/components/ResizeHandleWithTooltip';

import { AIChatSidebarTrigger } from '../components/ai/components/AIChatSidebarTrigger';
import { useCoPilot } from '../components/ai-service/CoPilotContext';
import { ErrorFallback } from '../components/ErrorFallback';
import SidebarLayout from './SidebarLayout';

export default function CoPilotLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { activeCoPilot, isCoPilotOpen, toggleCoPilot, closeCoPilot } =
    useCoPilot();

  // If no CoPilot is active, just use the regular SidebarLayout
  if (!activeCoPilot) {
    return <SidebarLayout>{children}</SidebarLayout>;
  }

  // When CoPilot is active, wrap the children in resizable panels
  // Note: Panel needs display:flex and flexDirection:column to properly
  // contain pages that use flex: 1 for their height
  const coPilotContent = (
    <PanelGroup direction="horizontal" id="copilot-panels">
      <Panel
        id="main-content"
        order={1}
        defaultSize={isCoPilotOpen ? 75 : 100}
        minSize={50}
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {children}
      </Panel>

      {/* Only render resize handle and CoPilot panel when CoPilot is open */}
      {isCoPilotOpen && (
        <>
          <ResizeHandleWithTooltip
            id="copilot-resize-handle"
            direction="horizontal"
          />
          <Panel
            id="copilot-sidebar"
            order={2}
            defaultSize={25}
            minSize={10}
            maxSize={40}
            collapsible
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  height: '100dvh',
                  borderLeft: (theme) => `0.5px solid ${theme.palette.grey30}`,
                  backgroundColor: (theme) => theme.palette.grey05,
                }}
              >
                <activeCoPilot.component onClose={closeCoPilot} />
              </Box>
            </ErrorBoundary>
          </Panel>
        </>
      )}
    </PanelGroup>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <SidebarLayout>{coPilotContent}</SidebarLayout>
      {/* Only show the trigger when CoPilot is active and not open */}
      {!isCoPilotOpen && (
        <AIChatSidebarTrigger isOpen={isCoPilotOpen} onClick={toggleCoPilot} />
      )}
    </Box>
  );
}
